/* ============================================================
 * 资源基址 / 站点基址（assetBase.js）
 *
 * 为什么要单独抽出来：
 *   打包用的是相对路径（vite.config.js 里 base: './'）。
 *   直接访问 https://wuzhe.online 一切正常，因为页面地址就是站点根目录；
 *   但把游戏挂到第三方平台（例如 VibeHub 的 iframe）里时，页面文档地址变成了
 *   平台的域名，`assets/img/player/player_rest_1.png` 这种「文档相对路径」会被
 *   浏览器拼到平台站点上：
 *     期望  https://wuzhe.online/assets/img/player/player_rest_1.png
 *     实际  https://apps.gamesvibe.app/xxx/assets/img/player/player_rest_1.png → 404
 *   图片对象变成 broken，canvas 再 drawImage 就抛 InvalidStateError。
 *
 * 解决办法：统一算出一个「资源基址」，所有运行时素材都从这里取。
 *   解析优先级：
 *     1. index.html 里的 <meta name="game-asset-base" content="https://.../">（显式指定，content 写 auto 视为不指定）
 *     2. JS 模块自己所在站点的根目录（模块在哪，public/assets 就在哪）
 *     3. 页面自己所在的自有域名根目录
 *     4. 兜底：正式域名 https://wuzhe.online/
 * ============================================================ */

export const CANONICAL_ORIGIN = 'https://wuzhe.online';

// 自有域名（wuzhe.online 及 www）——挂在这些域名下时素材就在同源根目录
export function isCanonicalHost(hostname) {
  return /(^|\.)wuzhe\.online$/i.test(String(hostname || '').trim());
}

// 自有域名 / 内网调试地址：这些情况下「页面在哪，素材就在哪」，不需要跨域
export function isOwnHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (!h) return true;                                  // file:// 等没有 host 的场景
  if (h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || h === '[::1]' || h === '::1') return true;
  if (/^192\.168\./.test(h) || /^10\./.test(h) || /^172\.(1[6-9]|2\d|3[01])\./.test(h)) return true;
  return isCanonicalHost(h);
}

function metaAssetBase() {
  if (typeof document === 'undefined') return '';
  const meta = document.querySelector('meta[name="game-asset-base"]');
  if (!meta) return '';
  const v = String(meta.getAttribute('content') || '').trim();
  if (!v || v === 'auto') return '';
  return v.replace(/\/+$/, '') + '/';
}

function moduleRoot() {
  try {
    // 开发时是 http://127.0.0.1:5173/src/main.js，打包后是 https://<站点>/assets/index-xxxx.js
    const here = new URL(import.meta.url);
    // 只有「模块也在自有/内网站点上」才敢直接用它的根目录；
    // 模块被第三方平台（VibeHub 等）托管时，这里会落到平台域名上，必须跳过。
    if (isOwnHost(here.hostname)) return new URL('../', here).href;
  } catch (e) { /* 拿不到就往下走兜底 */ }
  return '';
}

export const ASSET_BASE = (function () {
  const forced = metaAssetBase();
  if (forced) return forced;
  const root = moduleRoot();
  if (root) return root;
  if (typeof location !== 'undefined' && isOwnHost(location.hostname)) return location.origin + '/';
  return CANONICAL_ORIGIN + '/';
})();

/**
 * 把 `assets/img/xxx.png` 这类相对路径补成绝对地址。
 * http(s)://、data:、blob:、协议相对 `//`、根路径 `/` 原样返回。
 */
export function assetUrl(path) {
  const s = String(path == null ? '' : path);
  if (!s) return s;
  if (/^(?:[a-z][a-z0-9+.-]*:|\/\/|\/)/i.test(s)) return s;
  return ASSET_BASE + s.replace(/^\.\//, '');
}

/**
 * 首页那些图标写在 index.html 里，如果直接写 src="assets/...",
 * 浏览器会在解析 HTML 时就用「文档地址」去请求 —— 挂到第三方平台必然 404，
 * 而且这一批 404 会先于 JS 执行，控制台一片红。
 * 所以 index.html 里改成 data-asset="assets/..."，由这里在模块启动时补成绝对地址。
 * 只动图片，不动 <link>/<script>，避免让已加载好的 JS/CSS 再重新请求一遍。
 */
export function fixStaticAssetSrcs(root) {
  if (typeof document === 'undefined') return 0;
  const scope = root || document;
  let n = 0;

  // ① 主要路径：data-asset（不会产生任何错误请求）
  Array.prototype.forEach.call(scope.querySelectorAll('img[data-asset], source[data-asset]'), function (el) {
    const raw = el.getAttribute('data-asset');
    if (!raw) return;
    el.setAttribute('src', assetUrl(raw));
    n++;
  });

  // ② 兜底：万一还有别处写了相对路径的 src / srcset，也一并纠正
  Array.prototype.forEach.call(scope.querySelectorAll('img[src], source[src], source[srcset]'), function (el) {
    ['src', 'srcset'].forEach(function (attr) {
      const v = el.getAttribute(attr);
      if (!v || !/^(?:\.?\/)?assets\//.test(v)) return;
      el.setAttribute(attr, assetUrl(v.replace(/^\.?\//, '')));
      n++;
    });
  });
  return n;
}

/**
 * 兜底：任何一张素材缺失（404 / 解码失败）时，原生 drawImage 都会抛
 * InvalidStateError 并炸掉整帧渲染。这里给 drawImage 包一层，
 * 源图片没准备好就直接跳过 —— 顶多少画一个元素，不影响其它画面。
 */
export function guardDrawImage() {
  if (typeof window === 'undefined') return false;
  const Ctor = window.CanvasRenderingContext2D;
  if (!Ctor || !Ctor.prototype) return false;
  const proto = Ctor.prototype;
  if (proto.__assetGuardInstalled) return true;
  const raw = proto.drawImage;
  if (typeof raw !== 'function') return false;
  proto.drawImage = function (src) {
    if (src && typeof src === 'object' && 'complete' in src) {
      // HTMLImageElement：缺图 / 还在下载 / 解码失败 → 直接不画
      if (!src.complete || src.naturalWidth === 0) return;
    }
    try {
      return raw.apply(this, arguments);
    } catch (e) {
      return;   // 单张图画不出来也不该让整个游戏卡死
    }
  };
  proto.__assetGuardInstalled = true;
  return true;
}
